/**
 * As-you-type phone mask: (808) 555-1234 x8888
 *
 * Pure formatting layer — validation/normalization lives in shared/schema.ts
 * (parsePhone), which accepts this mask format as well as raw/pasted input.
 */

/** Format a raw input value to the mask. Digits beyond 10 become the extension. */
export function maskPhoneValue(raw: string): string {
  const lower = raw.toLowerCase().replace(/extension|ext\.?/g, 'x')
  const hasExplicitExt = /[x#]/.test(lower)
  const [numberPart = '', ...extParts] = lower.split(/[x#]/)

  let digits = numberPart.replace(/\D/g, '')
  let ext = extParts.join('').replace(/\D/g, '')

  if (digits.length > 10) {
    if (digits.length === 11 && digits.startsWith('1') && !ext) {
      digits = digits.slice(1) // pasted with leading country code
    } else {
      ext = digits.slice(10) + ext // overflow digits start the extension
      digits = digits.slice(0, 10)
    }
  }
  ext = ext.slice(0, 6)

  let out = ''
  if (digits.length > 0) out = `(${digits.slice(0, 3)}`
  if (digits.length >= 4) out += `) ${digits.slice(3, 6)}`
  if (digits.length >= 7) out += `-${digits.slice(6)}`
  if (ext) out += ` x${ext}`
  else if (hasExplicitExt && digits.length === 10) out += ' x' // user just typed "x"
  return out
}

/** Caret position = directly after the Nth digit of the formatted value. */
function caretAfterNthDigit(formatted: string, n: number): number {
  if (n <= 0) return formatted.startsWith('(') ? 1 : 0
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]!)) {
      seen++
      if (seen === n) return i + 1
    }
  }
  return formatted.length
}

function applyMask(input: HTMLInputElement): void {
  const before = input.value
  const caret = input.selectionStart ?? before.length

  // Track caret by digits only — the mask inserts 'x' itself, so counting it
  // would shift the caret left of freshly-typed extension digits.
  const digitsBeforeCaret = (before.slice(0, caret).match(/\d/g) ?? []).length
  const typedExtMarker = /[x#]|ext/i.test(before.slice(0, caret))

  const masked = maskPhoneValue(before)
  if (masked === before) return

  input.value = masked
  let pos = caretAfterNthDigit(masked, digitsBeforeCaret)
  // User explicitly typed the extension marker → land after " x".
  if (typedExtMarker && masked.slice(pos, pos + 2) === ' x') pos += 2
  input.setSelectionRange(pos, pos)
}

export function initPhoneMasks(form: HTMLFormElement): void {
  for (const input of form.querySelectorAll<HTMLInputElement>('input[type="tel"]')) {
    input.addEventListener('input', () => applyMask(input))
    // Normalize pre-filled / autofilled values too.
    if (input.value) applyMask(input)
  }
}
